import { Pencil, Truck, User, Route as RouteIcon, Clock, PenLine, StickyNote, Hash, Calculator } from 'lucide-react';
import { MarcaDelViaje } from './RevenuesTable';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import type { Revenue } from '../../types/finance';
import { useRevenueTransitions } from '../../hooks/useRevenueTransitions';
import { useRevenueAttachments } from '../../hooks/useRevenueAttachments';
import { RevenueTransitions } from './RevenueTransitions';
import { ReceiptViewer } from '../expenses/ReceiptViewer';
import { formatearCOP } from '../../utils/expenses';
import { formatearCantidad, cobradoDistintoDelPropuesto } from '../../utils/finance';
import { formatDateTime } from '../../utils/trips';

/**
 * FE-32 — el detalle de un ingreso: importe cobrado frente al propuesto, la
 * foto de la tarifa con la que se calculó, el soporte (reutiliza el visor del
 * recibo: es el mismo molde de adjuntos) y la bitácora.
 */
export function RevenueDetailModal({ revenue, onClose, onCorregir, puedeCorregir, ventanaVencida = false, puedeSubirSoporte, onSoporteSubido }: {
  revenue: Revenue | null;
  onClose: () => void;
  onCorregir?: (r: Revenue) => void;
  puedeCorregir: boolean;
  ventanaVencida?: boolean;
  puedeSubirSoporte: boolean;
  onSoporteSubido?: (r: Revenue) => void;
}) {
  const { transitions, isLoading, error } = useRevenueTransitions(revenue?.id ?? null);
  const soportes = useRevenueAttachments(revenue?.id ?? null);
  if (!revenue) return null;

  const foto = revenue.tariffSnapshot ?? {};
  const unidad = typeof foto.unit === 'string' ? foto.unit : undefined;
  const distinto = cobradoDistintoDelPropuesto(revenue);

  return (
    <Modal isOpen={!!revenue} onClose={onClose} title="Ingreso" size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cerrar</Button>
          {puedeCorregir && onCorregir && (
            <Button leftIcon={<Pencil size={14} />} onClick={() => onCorregir(revenue)} disabled={ventanaVencida}
              title={ventanaVencida ? 'Pasaron más de 2 semanas desde el registro: ya no se puede corregir.' : undefined}>
              Corregir
            </Button>
          )}
        </>
      }>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {puedeCorregir && ventanaVencida && (
          <div style={{ padding: '0.75rem 1rem', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-lg)', color: '#F59E0B', fontSize: '12.5px' }}>
            Pasaron más de 2 semanas desde el registro — este ingreso quedó en solo lectura.
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: '12px', fontWeight: 800, color: '#10B981', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.35)', borderRadius: '99px', padding: '3px 11px' }}>
            {revenue.customerName}
          </span>
          {revenue.tariffName && <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{revenue.tariffName}</span>}
          {revenue.origin === 'TRIP_START' && <MarcaDelViaje />}
          <strong style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', marginLeft: 'auto' }}>{formatearCOP(revenue.amount)}</strong>
        </div>

        {revenue.computedAmount && (
          <div style={{ padding: '0.75rem 0.875rem', background: distinto ? 'rgba(139,92,246,0.10)' : 'var(--bg-raised)', border: `1px solid ${distinto ? 'rgba(139,92,246,0.35)' : 'var(--border)'}`, borderRadius: 'var(--radius-lg)', fontSize: '12.5px', color: 'var(--text-secondary)', display: 'flex', gap: '0.625rem', alignItems: 'flex-start' }}>
            <Calculator size={16} style={{ flexShrink: 0, marginTop: '1px', color: distinto ? '#8B5CF6' : 'var(--text-muted)' }} />
            <span>
              La tarifa propuso <strong style={{ color: 'var(--text-primary)' }}>{formatearCOP(revenue.computedAmount)}</strong>
              {revenue.quantity && <> por {formatearCantidad(revenue.quantity, unidad)}</>}
              {typeof foto.discountPct === 'string' && foto.discountPct !== '0.00' && <> (descuento {foto.discountPct} %)</>}
              {typeof foto.minimumAmount === 'string' && <>, mínimo {formatearCOP(foto.minimumAmount)}</>}.
              {distinto
                ? <> <strong style={{ color: '#8B5CF6' }}>Se cobró otra cosa</strong>: la razón está en la bitácora.</>
                : <> Se cobró lo propuesto.</>}
            </span>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.625rem' }}>
          <Dato icono={<Clock size={13} />} etiqueta="Se prestó" valor={formatDateTime(revenue.occurredAt)} />
          <Dato icono={<PenLine size={13} />} etiqueta="Se registró" valor={formatDateTime(revenue.createdAt)} />
          {revenue.vehiclePlate && <Dato icono={<Truck size={13} />} etiqueta="Vehículo" valor={revenue.vehiclePlate} />}
          {revenue.driverName && <Dato icono={<User size={13} />} etiqueta="Conductor" valor={revenue.driverName} />}
          {revenue.tripId && <Dato icono={<RouteIcon size={13} />} etiqueta="Viaje" valor="asignado explícitamente" />}
          {revenue.reference && <Dato icono={<Hash size={13} />} etiqueta="Referencia" valor={revenue.reference} />}
          {revenue.notes && <Dato icono={<StickyNote size={13} />} etiqueta="Observación" valor={revenue.notes} />}
        </div>

        <ReceiptViewer
          receipts={soportes.attachments}
          isLoading={soportes.isLoading}
          error={soportes.error}
          subiendo={soportes.subiendo}
          urlDeDescarga={soportes.urlDeDescarga}
          onSubir={puedeSubirSoporte ? async (file) => {
            const actualizado = await soportes.subir(file);
            onSoporteSubido?.(actualizado);
          } : undefined}
        />

        <RevenueTransitions transitions={transitions} isLoading={isLoading} error={error} />
      </div>
    </Modal>
  );
}

function Dato({ icono, etiqueta, valor }: { icono: React.ReactNode; etiqueta: string; valor: string }) {
  return (
    <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '0.5rem 0.75rem' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--text-muted)' }}>{icono} {etiqueta}</span>
      <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{valor}</span>
    </div>
  );
}
